import mqttService from './mqttService';
import { getSelectedRfidDevice } from '../utils/rfidDeviceUtils';

/**
 * Background Worker Service for Barcode Input
 * 
 * This service runs in the background and:
 * 1. Monitors localStorage for handheld device settings
 * 2. Subscribes to MQTT barcode topic when device is configured
 * 3. Automatically injects received barcode data into focused input fields
 */
class BarcodeWorkerService {
  constructor() {
    if (BarcodeWorkerService.instance) {
      return BarcodeWorkerService.instance;
    }

    this.isRunning = false;
    this.currentTopic = null;
    this.checkInterval = null;
    this.mqttMessageHandler = null;
    this.focusHandler = null;
    this.currentFocusedElement = null;

    // Debounce mechanism to prevent multiple injections
    this.lastBarcodeData = null;
    this.lastInjectionTime = 0;
    this.injectionDebounceTime = 1000; // 1 second debounce
    this.isTyping = false; // Flag to prevent injection during typing

    // Configuration
    this.CHECK_INTERVAL = 2000; // Check localStorage every 2 seconds
    this.TOPIC_PREFIX = 'handheld/test/barcode'; // Base topic for barcode messages

    BarcodeWorkerService.instance = this;
  }

  /**
   * Start the background worker
   */
  start() {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;

    // Start monitoring localStorage for device settings
    this.startDeviceMonitoring();

    // Setup focus tracking
    this.setupFocusTracking();

    // Setup MQTT message handling
    this.setupMQTTMessageHandling();
  }

  /**
   * Stop the background worker
   */
  stop() {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;

    // Clear monitoring interval
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    // Unsubscribe from current topic
    this.unsubscribeFromCurrentTopic();

    // Remove event listeners
    this.removeEventListeners();
  }

  /**
   * Start monitoring localStorage for device settings
   */
  startDeviceMonitoring() {
    // Check immediately
    this.checkDeviceSettings();

    // Set up periodic checking
    this.checkInterval = setInterval(() => {
      this.checkDeviceSettings();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Check localStorage for handheld device settings
   */
  checkDeviceSettings() {
    try {
      // Check for handheld device in audit operation (as shown in the image)
      const handheldDevice = getSelectedRfidDevice('audit');
      if (handheldDevice) {
        // Device is configured, subscribe to barcode topic
        const topic = handheldDevice.replace('/rfid', '/barcode');

        // Only subscribe if topic has changed
        if (this.currentTopic !== topic) {
          this.subscribeToBarcodeTopic(topic, handheldDevice);
        }
      } else {
        // No device configured, unsubscribe if currently subscribed
        this.unsubscribeFromCurrentTopic();
      }
    } catch (error) {
      console.error('[BarcodeWorker] Error checking device settings:', error);
    }
  }

  /**
   * Subscribe to barcode topic
   */
  subscribeToBarcodeTopic(topic, deviceId) {
    // Only subscribe if not already subscribed to this topic
    if (this.currentTopic === topic) {
      return;
    }

    // Unsubscribe from previous topic if any
    this.unsubscribeFromCurrentTopic();

    // Subscribe to the topic
    const success = mqttService.subscribe(topic);

    if (success) {
      this.currentTopic = topic;
    } else {
      console.error(`[BarcodeWorker] Failed to subscribe to ${topic}`);
    }
  }

  /**
   * Unsubscribe from current topic
   */
  unsubscribeFromCurrentTopic() {
    if (this.currentTopic) {
      mqttService.unsubscribe(this.currentTopic);
      this.currentTopic = null;
    }
  }

  /**
   * Setup MQTT message handling
   */
  setupMQTTMessageHandling() {
    this.mqttMessageHandler = (event) => {
      const { topic, message } = event.detail;
      // Only handle messages from our subscribed barcode topic
      if (topic === this.currentTopic) {
        this.handleBarcodeMessage(message);
      }
    };

    // Listen for MQTT messages
    window.addEventListener('mqttMessage', this.mqttMessageHandler);
  }

  /**
   * Handle received barcode message
   */
  handleBarcodeMessage(message) {
    try {
      // Extract barcode data from message
      let barcodeData = '';

      if (typeof message === 'string') {
        barcodeData = message;
      } else if (typeof message === 'object' && message !== null) {
        // Try to extract barcode from common fields
        barcodeData = message.barcode || message.data || message.value || message.code || '';
      }

      if (barcodeData) {
        // Check if we should inject this barcode (debounce mechanism)
        if (this.shouldInjectBarcode(barcodeData)) {
          this.injectBarcodeToFocusedInput(barcodeData);
        }
      } else {
        console.warn('[BarcodeWorker] Received message but no barcode data found:', message);
      }
    } catch (error) {
      console.error('[BarcodeWorker] Error handling barcode message:', error);
    }
  }

  /**
   * Check if barcode should be injected (debounce mechanism)
   */
  shouldInjectBarcode(barcodeData) {
    const currentTime = Date.now();

    // Don't inject if we're currently typing
    if (this.isTyping) {
      return false;
    }

    // Check if it's the same barcode and within debounce time
    if (this.lastBarcodeData === barcodeData &&
      (currentTime - this.lastInjectionTime) < this.injectionDebounceTime) {
      return false;
    }

    // Update tracking variables
    this.lastBarcodeData = barcodeData;
    this.lastInjectionTime = currentTime;

    return true;
  }

  /**
   * Setup focus tracking to detect currently focused input
   */
  setupFocusTracking() {
    this.focusHandler = (event) => {
      const target = event.target;

      // Check if the focused element is an input field
      if (this.isInputField(target)) {
        this.currentFocusedElement = target;
      }
    };

    const blurHandler = (event) => {
      const target = event.target;

      // Clear focused element if it's the one losing focus
      if (this.currentFocusedElement === target) {
        this.currentFocusedElement = null;
      }
    };

    // Listen for focus and blur events
    document.addEventListener('focusin', this.focusHandler, true);
    document.addEventListener('focusout', blurHandler, true);
  }

  /**
   * Check if element is an input field
   */
  isInputField(element) {
    if (!element || !element.tagName) {
      return false;
    }

    const tagName = element.tagName.toLowerCase();
    const inputTypes = ['text', 'password', 'email', 'number', 'search', 'tel', 'url'];

    // Check for input elements
    if (tagName === 'input') {
      const type = element.type.toLowerCase();
      return inputTypes.includes(type) || !type; // Default type is 'text'
    }

    // Check for textarea elements
    if (tagName === 'textarea') {
      return true;
    }

    // Check for contenteditable elements
    if (element.contentEditable === 'true') {
      return true;
    }

    // Check for autocomplete components (common patterns)
    if (this.isAutocompleteComponent(element)) {
      return true;
    }

    return false;
  }

  /**
   * Check if element is an autocomplete component
   */
  isAutocompleteComponent(element) {
    if (!element) return false;

    // Check for common autocomplete class names
    const autocompleteClasses = [
      'autocomplete',
      'autocomplete-input',
      'react-autocomplete',
      'mui-autocomplete',
      'ant-select',
      'select2-container'
    ];

    const className = element.className || '';
    const hasAutocompleteClass = autocompleteClasses.some(cls =>
      className.toLowerCase().includes(cls.toLowerCase())
    );

    // Check for autocomplete attributes
    const hasAutocompleteAttr = element.hasAttribute('autocomplete') ||
      element.hasAttribute('data-autocomplete') ||
      element.hasAttribute('role') && element.getAttribute('role') === 'combobox';

    // Check for parent autocomplete containers
    const parent = element.parentElement;
    const hasAutocompleteParent = parent && (
      autocompleteClasses.some(cls =>
        parent.className && parent.className.toLowerCase().includes(cls.toLowerCase())
      ) ||
      parent.hasAttribute('data-autocomplete') ||
      parent.getAttribute('role') === 'combobox'
    );

    return hasAutocompleteClass || hasAutocompleteAttr || hasAutocompleteParent;
  }

  /**
   * Inject barcode data into the currently focused input field using HID simulation
   */
  injectBarcodeToFocusedInput(barcodeData) {
    if (!this.currentFocusedElement) {
      return;
    }

    try {
      const element = this.currentFocusedElement;

      // Use HID-style character-by-character input for better autocomplete support
      this.simulateHIDInput(element, barcodeData);

    } catch (error) {
      console.error('[BarcodeWorker] Error injecting barcode to input:', error);
    }
  }

  /**
   * Simulate HID (Human Interface Device) input by typing each character individually
   */
  async simulateHIDInput(element, text) {
    // Set typing flag to prevent concurrent injections
    if (window.isTyping) {
      return;
    }
    window.isTyping = true;
    try {
      // Ensure element is focused
      element.focus();
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        await this.typeCharacter(element, char, i);
        await this.delay(20); // 50ms delay between characters
      }
      await this.delay(200);
      element.focus();
      this.dispatchFinalEvents(element, text);
    } finally {
      window.isTyping = false;
      if(window.handleSearchTermChange) {
        window.handleSearchTermChange(text);
      }
    }
  }

  /**
   * Type a single character with full event simulation
   */
  async typeCharacter(element, char, position) {
    // Create keyboard events for the character
    const keyCode = char.charCodeAt(0);
    const key = char;
    const code = this.getKeyCode(char);

    // Keydown event
    const keydownEvent = new KeyboardEvent('keydown', {
      bubbles: true,
      cancelable: true,
      key: key,
      code: code,
      keyCode: keyCode,
      which: keyCode,
      charCode: keyCode
    });

    // Keypress event (for printable characters)
    const keypressEvent = new KeyboardEvent('keypress', {
      bubbles: true,
      cancelable: true,
      key: key,
      code: code,
      keyCode: keyCode,
      which: keyCode,
      charCode: keyCode
    });

    // Input event
    const inputEvent = new InputEvent('input', {
      bubbles: true,
      cancelable: true,
      inputType: 'insertText',
      data: char,
      isComposing: false
    });

    // Keyup event
    const keyupEvent = new KeyboardEvent('keyup', {
      bubbles: true,
      cancelable: true,
      key: key,
      code: code,
      keyCode: keyCode,
      which: keyCode,
      charCode: keyCode
    });

    // Dispatch events in sequence
    const events = [keydownEvent, keypressEvent, inputEvent, keyupEvent];

    for (const event of events) {
      try {
        element.dispatchEvent(event);
      } catch (error) {
        console.warn('[BarcodeWorker] Error dispatching character event:', event.type, error);
      }
    }

    // Update the element value
    if (element.contentEditable === 'true') {
      element.textContent += char;
    } else {
      element.value += char;
    }

    // Trigger React synthetic events if available
    this.triggerReactSyntheticEvents(element, char);
  }

  /**
   * Get key code for character
   */
  getKeyCode(char) {
    const keyMap = {
      '0': 'Digit0', '1': 'Digit1', '2': 'Digit2', '3': 'Digit3', '4': 'Digit4',
      '5': 'Digit5', '6': 'Digit6', '7': 'Digit7', '8': 'Digit8', '9': 'Digit9',
      'A': 'KeyA', 'B': 'KeyB', 'C': 'KeyC', 'D': 'KeyD', 'E': 'KeyE',
      'F': 'KeyF', 'G': 'KeyG', 'H': 'KeyH', 'I': 'KeyI', 'J': 'KeyJ',
      'K': 'KeyK', 'L': 'KeyL', 'M': 'KeyM', 'N': 'KeyN', 'O': 'KeyO',
      'P': 'KeyP', 'Q': 'KeyQ', 'R': 'KeyR', 'S': 'KeyS', 'T': 'KeyT',
      'U': 'KeyU', 'V': 'KeyV', 'W': 'KeyW', 'X': 'KeyX', 'Y': 'KeyY', 'Z': 'KeyZ',
      'a': 'KeyA', 'b': 'KeyB', 'c': 'KeyC', 'd': 'KeyD', 'e': 'KeyE',
      'f': 'KeyF', 'g': 'KeyG', 'h': 'KeyH', 'i': 'KeyI', 'j': 'KeyJ',
      'k': 'KeyK', 'l': 'KeyL', 'm': 'KeyM', 'n': 'KeyN', 'o': 'KeyO',
      'p': 'KeyP', 'q': 'KeyQ', 'r': 'KeyR', 's': 'KeyS', 't': 'KeyT',
      'u': 'KeyU', 'v': 'KeyV', 'w': 'KeyW', 'x': 'KeyX', 'y': 'KeyY', 'z': 'KeyZ',
      ' ': 'Space', '-': 'Minus', '_': 'Minus', '.': 'Period', ',': 'Comma'
    };

    return keyMap[char] || `Key${char.toUpperCase()}`;
  }

  /**
   * Trigger React synthetic events
   */
  triggerReactSyntheticEvents(element, char) {
    // Try to trigger React's onChange handler
    const reactProps = element._reactProps ||
      element._reactInternalFiber?.memoizedProps ||
      element._reactInternalInstance?.current?.memoizedProps;

    if (reactProps) {
      // Trigger onChange
      if (reactProps.onChange) {
        const syntheticEvent = {
          target: element,
          currentTarget: element,
          type: 'change',
          bubbles: true,
          cancelable: true
        };
        reactProps.onChange(syntheticEvent);
      }

      // Trigger onInput
      if (reactProps.onInput) {
        const syntheticInputEvent = {
          target: element,
          currentTarget: element,
          type: 'input',
          bubbles: true,
          cancelable: true,
          data: char
        };
        reactProps.onInput(syntheticInputEvent);
      }
    }
  }

  /**
   * Dispatch final events after all characters are typed
   */
  dispatchFinalEvents(element, fullText) {
    const finalEvents = [
      // Final input event with full text
      new InputEvent('input', {
        bubbles: true,
        cancelable: true,
        inputType: 'insertText',
        data: fullText
      }),

      // Change event for form validation
      new Event('change', { bubbles: true, cancelable: true }),

      // Custom barcode injection event
      new CustomEvent('barcodeInjected', {
        bubbles: true,
        cancelable: true,
        detail: { barcode: fullText }
      })
    ];

    finalEvents.forEach(event => {
      try {
        element.dispatchEvent(event);
      } catch (error) {
        console.warn('[BarcodeWorker] Error dispatching final event:', event.type, error);
      }
    });

    // Ensure element remains focused
    element.focus();
  }

  /**
   * Utility function to create delays
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }


  /**
   * Remove event listeners
   */
  removeEventListeners() {
    if (this.mqttMessageHandler) {
      window.removeEventListener('mqttMessage', this.mqttMessageHandler);
      this.mqttMessageHandler = null;
    }

    if (this.focusHandler) {
      document.removeEventListener('focusin', this.focusHandler, true);
      document.removeEventListener('focusout', this.focusHandler, true);
      this.focusHandler = null;
    }
  }

  /**
   * Get current status of the worker
   */
  getStatus() {
    return {
      isRunning: this.isRunning,
      currentTopic: this.currentTopic,
      currentFocusedElement: this.currentFocusedElement ? {
        tagName: this.currentFocusedElement.tagName,
        type: this.currentFocusedElement.type,
        id: this.currentFocusedElement.id,
        className: this.currentFocusedElement.className
      } : null,
      mqttConnected: mqttService.getConnectionStatus().isConnected,
      isTyping: this.isTyping,
      lastBarcodeData: this.lastBarcodeData,
      lastInjectionTime: this.lastInjectionTime
    };
  }

  /**
   * Reset debounce mechanism (useful for testing)
   */
  resetDebounce() {
    this.lastBarcodeData = null;
    this.lastInjectionTime = 0;
    this.isTyping = false;
  }

  /**
   * Test the worker functionality
   */
  test() {
    const device = getSelectedRfidDevice('audit');
    const mqttStatus = mqttService.getConnectionStatus();
    const status = this.getStatus();
    const hasFocusedInput = Boolean(this.currentFocusedElement);

    if (hasFocusedInput) {
      this.injectBarcodeToFocusedInput('TEST_BARCODE_123');
    }

    return {
      device,
      mqttStatus,
      status,
      injectionAttempted: hasFocusedInput
    };
  }
}

// Create and export singleton instance
const barcodeWorkerService = new BarcodeWorkerService();

// Auto-start the worker when MQTT is connected
// This ensures the worker starts only after MQTT connection is established
if (typeof window !== 'undefined') {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeWorker();
    });
  } else {
    initializeWorker();
  }

  // Expose for debugging
  window.barcodeWorkerService = barcodeWorkerService;
}

/**
 * Initialize the worker after MQTT connection is established
 */
function initializeWorker() {
  // Check if MQTT is already connected
  const mqttStatus = mqttService.getConnectionStatus();
  if (mqttStatus.isConnected) {
    barcodeWorkerService.start();
  } else {
    // Wait for MQTT connection
    const checkMQTTConnection = () => {
      const status = mqttService.getConnectionStatus();
      if (status.isConnected) {
        barcodeWorkerService.start();
      } else {
        // Check again after 1 second
        setTimeout(checkMQTTConnection, 1000);
      }
    };

    // Start checking after a short delay
    setTimeout(checkMQTTConnection, 500);
  }
}

export default barcodeWorkerService;
